import { Component } from '@angular/core';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { DcValueAccessorComponent } from '@datachain/ui-sdk/common';
import { takeUntil, tap } from 'rxjs';

import { ValidationErrorKeys } from '../../../ui/form/validation-erros-keys';
import { ConnectorMode } from '../export-template-details/export-connector.entity';
import { ExportTemplateDetailsHelper } from '../export-template-details/export-template-details.helper';
import {
	ExportWriteModeConfigurationEntity,
	WriteMode,
} from './export-write-mode-configuration.entity';

interface IWriteModeOptions {
	label: string;
	value: string;
	disabled?: boolean;
}

enum ExportWriteModeFormControls {
	Mode = 'mode',
}

@Component({
	selector: 'app-export-write-mode-configuration',
	templateUrl: './export-write-mode-configuration.component.html',
	styleUrls: ['./export-write-mode-configuration.component.scss'],
	providers: [
		DcValueAccessorComponent.generateAccessorToken(
			ExportWriteModeConfigurationComponent
		),
		DcValueAccessorComponent.generateValidatorToken(
			ExportWriteModeConfigurationComponent
		),
	],
})
export class ExportWriteModeConfigurationComponent extends DcValueAccessorComponent<
	ExportWriteModeConfigurationComponent,
	ExportWriteModeConfigurationEntity
> {
	public Appearance: MatFormFieldAppearance = 'outline';
	public ExportWriteModeFormControls = ExportWriteModeFormControls;
	public ValidationErrorKeys = ValidationErrorKeys;
	public entity: ExportWriteModeConfigurationEntity | null = null;
	public writeOptions: Array<IWriteModeOptions> = [];

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private static toEntity(formValues: any): ExportWriteModeConfigurationEntity {
		return ExportWriteModeConfigurationEntity.build({
			mode: formValues[ExportWriteModeFormControls.Mode],
			isEnabled:
				formValues[ExportWriteModeFormControls.Mode] !== WriteMode.None,
		});
	}
	public constructor(
		private readonly fb: FormBuilder,
		private readonly exportTemplateDetailsHelper: ExportTemplateDetailsHelper
	) {
		super();
		this.cmpId = 'export-write-mode-config';

		this.form = this.fb.group({
			[ExportWriteModeFormControls.Mode]: [WriteMode.None, Validators.required],
		});

		this.form
			.get(ExportWriteModeFormControls.Mode)
			?.valueChanges.pipe(
				takeUntil(this.onDestroy$),
				tap((val) => this.exportTemplateDetailsHelper.setSelectedWriteMode(val))
			)
			.subscribe();

		this.form.valueChanges
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => {
					this.onChange(this.extract());
					Object.keys(this.form.controls).forEach((k) => {
						const ctrl = this.form.get(k) as AbstractControl;
						ctrl.markAsTouched();
					});
				})
			)
			.subscribe();

		this.exportTemplateDetailsHelper.selectedConnectorType$
			.pipe(
				takeUntil(this.onDestroy$),
				tap((connectorType) => {
					if (
						connectorType === ConnectorMode.Neo4j ||
						connectorType === ConnectorMode.NoSQL
					) {
						this.writeOptions = [
							{
								label: $localize`:i18n=@@export.writeMode.option.append:`,
								value: WriteMode.Append,
							},
							{
								label: $localize`:i18n=@@export.writeMode.option.overwrite:`,
								value: WriteMode.Overwrite,
							},
						];
						return;
					}
					this.writeOptions = [
						{
							label: $localize`:i18n=@@export.writeMode.option.append:`,
							value: WriteMode.Append,
						},
						{
							label: $localize`:i18n=@@export.writeMode.option.overwrite:`,
							value: WriteMode.Overwrite,
						},
						{
							label: $localize`:i18n=@@export.writeMode.option.truncate:`,
							value: WriteMode.Truncate,
						},
					];
				})
			)
			.subscribe();
	}

	public extract(): ExportWriteModeConfigurationEntity {
		const formValues = this.form.value;
		return ExportWriteModeConfigurationComponent.toEntity(formValues);
	}

	protected writeIntoForm(obj: ExportWriteModeConfigurationEntity): void {
		this.entity = obj;
		this.form.patchValue({
			[ExportWriteModeFormControls.Mode]: obj.mode,
		});
	}
}
